import * as React from 'react';
import { AppCard } from './app-card';
import type {
  embeddedScriptAppStatus,
  IEmbeddedScriptAppStatus,
  Origin,
} from '../manage-installed-apps-page';
import type { IModalProps, ModalNames } from '../modals/modals';
import { Card } from '@wix/design-system';
import type { BundleWebSolution } from '@wix/ambassador-marketplace/types';
import { useBiLogger } from '../../hooks/use-bi-logger';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { openAppClick } from '@wix/bi-logger-market/v2';

const isLastAppCard = (apps: any[], i: number) => {
  return apps.length - 1 === i;
};

export interface IAppsSectionProps {
  children: JSX.Element;
  apps: (ManagedApp | BundleWebSolution)[];
  bundleApps: BundleWebSolution[];
  isEditorless?: boolean;
  mainButtonAction(
    app: ManagedApp | BundleWebSolution,
    bundleApps: BundleWebSolution[],
  ): {} | void;
  origin: Origin;
  msid: string;
  addAppAgain?(appDefID: string, appVersion: string): void;
  getAppInstance?(appDefID: string): string;
  updateEmbeddedScriptAppsStatus?(
    appDefId: string,
    newStatus: embeddedScriptAppStatus,
    appInstanceId: string,
    embeddedScriptAppsStatus: { [key: string]: IEmbeddedScriptAppStatus },
  );
  embeddedScriptAppsStatus?: { [key: string]: IEmbeddedScriptAppStatus };
  openModal?(modalName: ModalNames, modalProps: IModalProps): void;
  baseUrl: string;
  deleteApp(app: ManagedApp | BundleWebSolution): void;
  isBusinessApp: boolean;
  editorType: string;
  gotoWebSolutionPage(slug: string): void;
  siteIsPremium: boolean;
  isActionRequiredSection?: boolean;
  openApp?(app: ManagedApp): Promise<{} | void>;
  deleteAppFromBundle?(appId: string): void;
  dataHook?: string;
  locale?: string;
  siteIsBusinessFirstSite?: boolean;
  showUserActionNotification?(INotificationsOptions);
  authorizationHeader?: any;
  editorUrl?: string;
  openDeleteModalAppInEditor?(appId: string): void;
}

export function AppsSection({
  apps,
  mainButtonAction,
  children,
  msid,
  origin,
  addAppAgain,
  getAppInstance,
  updateEmbeddedScriptAppsStatus,
  embeddedScriptAppsStatus,
  openModal,
  baseUrl,
  deleteApp,
  isBusinessApp,
  editorType,
  gotoWebSolutionPage,
  siteIsPremium,
  isActionRequiredSection,
  openApp,
  deleteAppFromBundle,
  dataHook,
  locale,
  siteIsBusinessFirstSite,
  bundleApps,
  isEditorless,
  showUserActionNotification,
  authorizationHeader,
  editorUrl,
  openDeleteModalAppInEditor,
}: IAppsSectionProps) {
  const biLogger = useBiLogger();
  const [SectionHeader] = React.Children.toArray(children);

  return (
    <div>
      {apps?.length ? (
        <div style={{ marginBottom: '24px' }}>
          <Card dataHook={dataHook}>
            {React.cloneElement(SectionHeader as React.ReactElement)}
            <Card.Divider />
            {apps.map((app: ManagedApp, i) => (
              <div
                key={app.appId}
                onClick={
                  isEditorless
                    ? undefined
                    : () => {
                        biLogger
                          .report(
                            openAppClick({
                              index: i,
                              msid,
                              app_id: app.appId,
                              market: origin,
                              tag_type: 'manage_apps',
                              tag_name: 'manage_apps_app_row',
                            }),
                          )
                          .catch((e) => console.log(e));
                        mainButtonAction(app, bundleApps);
                      }
                }
              >
                <AppCard
                  isEditorless={isEditorless}
                  app={app}
                  msid={msid}
                  origin={origin}
                  baseUrl={baseUrl}
                  editorType={editorType}
                  addAppAgain={addAppAgain}
                  getAppInstance={getAppInstance}
                  embeddedScriptAppsStatus={embeddedScriptAppsStatus}
                  updateEmbeddedScriptAppsStatus={
                    updateEmbeddedScriptAppsStatus
                  }
                  deleteApp={deleteApp}
                  mainButtonAction={mainButtonAction}
                  openModal={openModal}
                  isBusinessApp={isBusinessApp}
                  index={i}
                  gotoWebSolutionPage={gotoWebSolutionPage}
                  siteIsPremium={siteIsPremium}
                  isActionRequiredApp={isActionRequiredSection}
                  openApp={openApp}
                  deleteAppFromBundle={deleteAppFromBundle}
                  locale={locale}
                  siteIsBusinessFirstSite={siteIsBusinessFirstSite}
                  bundleApps={bundleApps}
                  showUserActionNotification={showUserActionNotification}
                  editorUrl={editorUrl}
                  authorizationHeader={authorizationHeader}
                  openDeleteModalAppInEditor={openDeleteModalAppInEditor}
                />
                {!isLastAppCard(apps, i) && <Card.Divider />}
              </div>
            ))}
          </Card>
        </div>
      ) : null}
    </div>
  );
}
